import { getUserAttributes } from '@noths/polaris-client-user-configuration';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import type { Environment } from 'src/constants/environment';
import type { AddToBasketResponse, Basket, GetBasketWithOptionsResponse } from 'src/types/Basket';
import { logger } from 'src/utils/serverLogger';
import { endpoints } from './endpoints';
import { ADD_MEMBERSHIP_ITEM_MUTATION } from './mutations/addMembershipItemToBasket';
import { BASKET_QUERY } from './queries/basket';

interface BasketBody {
  body: {
    query: string;
    variables?: Record<string, string | number>;
  };
  headers?: Record<string, string>;
}

const apiName = 'basket-api';

const addBasketEndpoint = (env: Environment) => {
  window.Amplify.Configure.API.REST.addEndpoint({ name: apiName, endpoint: endpoints[env].basket });
};

export const addMembershipProductToBasket = async (env: Environment, currency: CurrencyCode) => {
  try {
    addBasketEndpoint(env);

    const attributes = await getUserAttributes();

    const idToken = attributes?.idToken;

    const {
      data: {
        addMembershipItem: { basket, errors, success },
      },
    } = await window.Amplify.default.API.post<BasketBody, AddToBasketResponse>(
      apiName,
      '/graphql',
      {
        headers: {
          ...(idToken && {
            'x-id-token': idToken,
          }),
        },
        body: {
          query: ADD_MEMBERSHIP_ITEM_MUTATION,
          variables: { currency },
        },
      },
    );

    const membershipIsAlreadyInBasket =
      errors.length === 1 && errors[0].code === 'ALREADY_CONTAINS_MEMBERSHIP_PRODUCT';

    if (success || membershipIsAlreadyInBasket) {
      return basket;
    }

    throw new Error(errors[0].code);
  } catch (error) {
    logger.error('error in adding membership product to the basket:', {
      error: `${String(error)}`,
    });
    throw error;
  }
};

export const getBasket = async (env: Environment, currency: CurrencyCode): Promise<Basket> => {
  try {
    addBasketEndpoint(env);

    const attributes = await getUserAttributes();

    const idToken = attributes?.idToken;

    const {
      data: { basket },
    } = await window.Amplify.default.API.post<BasketBody, GetBasketWithOptionsResponse>(
      apiName,
      '/graphql',
      {
        headers: {
          ...(idToken && {
            'x-id-token': idToken,
          }),
        },
        body: {
          query: BASKET_QUERY,
          variables: { currency },
        },
      },
    );
    return basket;
  } catch (error) {
    logger.error('error in getting Basket items:', { error: `${String(error)}` });
    throw error;
  }
};
